import React, { useState, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useCRM } from '../context/CRMContext';

const SOURCE_ICONS = { facebook: '📘', instagram: '📸', whatsapp: '💬', website: '🌐', referral: '🤝', manual: '✍️' };

export default function Pipeline() {
  const { leads, pipelineStages, updateLead, getUserById } = useCRM();
  const [selectedLead, setSelectedLead] = useState(null);

  // Group leads by stage
  const columns = useMemo(() => {
    const cols = {};
    pipelineStages.forEach(s => {
      cols[s.id] = { ...s, leads: [] };
    });
    const firstStageId = pipelineStages[0]?.id;
    leads.forEach(l => {
      if (cols[l.status]) cols[l.status].leads.push(l);
      else if (firstStageId && cols[firstStageId]) cols[firstStageId].leads.push(l);
    });
    return cols;
  }, [leads, pipelineStages]);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    updateLead(draggableId, { status: destination.droppableId });
  };

  const totalValue = leads.reduce((acc, l) => acc + (Number(l.budget) || 0), 0);

  const LeadCard = ({ lead, index }) => {
    const user = getUserById(lead.assignedTo);
    const scoreColor = lead.score >= 80 ? '#10b981' : lead.score >= 60 ? '#f59e0b' : '#ef4444';

    return (
      <Draggable draggableId={lead.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="kanban-card"
            onClick={() => setSelectedLead(lead)}
            style={{
              ...provided.draggableProps.style,
              opacity: snapshot.isDragging ? 0.85 : 1,
              boxShadow: snapshot.isDragging ? '0 12px 32px rgba(0,0,0,0.5)' : undefined,
            }}
          >
            {/* Priority strip */}
            <div style={{
              height: 3, borderRadius: 2, marginBottom: 10,
              background: lead.priority === 'hot' ? 'linear-gradient(90deg, #ef4444, #f59e0b)'
                        : lead.priority === 'warm' ? 'linear-gradient(90deg, #f59e0b, #10b981)'
                        : 'linear-gradient(90deg, #3b82f6, #06b6d4)',
            }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.course || 'No course'} · {lead.country || '?'}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor, flexShrink: 0, marginLeft: 8 }}>{lead.score}</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>{SOURCE_ICONS[lead.source]}</span>
                {lead.intake && (
                  <span style={{ fontSize: 10, color: '#64748b', background: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: 4 }}>{lead.intake}</span>
                )}
              </div>
              {user && (
                <div className="avatar" style={{ background: user.avatar?.bg || '#7c3aed', color: user.avatar?.color || '#fff', width: 22, height: 22, fontSize: 9 }}>
                  {(user.name || '?').split(' ').map(n => n[0]).join('')}
                </div>
              )}
            </div>

            {lead.budget && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.05)', fontSize: 11, color: '#64748b' }}>
                💰 ₹{Number(lead.budget).toLocaleString('en-IN')}
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Top stats */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 20, alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'Total Leads', value: leads.length, color: '#7c3aed' },
            { label: 'Pipeline Value', value: `₹${(totalValue / 10000000).toFixed(1)}Cr`, color: '#10b981' },
            { label: 'Hot Leads', value: leads.filter(l => l.priority === 'hot').length, color: '#ef4444' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
              <span style={{ fontSize: 12, color: '#64748b' }}>{s.label}:</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#475569' }}>
          Drag cards to move leads between stages
        </div>
      </div>

      {/* Kanban board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', padding: '16px 24px', display: 'flex', gap: 14 }}>
          {pipelineStages.map(stage => {
            const col = columns[stage.id];
            if (!col) return null;
            const colLeads = col.leads;
            const colValue = colLeads.reduce((a, l) => a + (Number(l.budget) || 0), 0);

            return (
              <Droppable droppableId={stage.id} key={stage.id}>
                {(provided, snapshot) => (
                  <div
                    className="kanban-col"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      borderTop: `3px solid ${stage.color}`,
                      background: snapshot.isDraggingOver ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.02)',
                      transition: 'background 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: '100%',
                    }}
                  >
                    {/* Column header */}
                    <div style={{ marginBottom: 12, flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{stage.name}</span>
                        </div>
                        <span style={{
                          background: `${stage.color}25`, color: stage.color,
                          borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                        }}>{colLeads.length}</span>
                      </div>
                      {colValue > 0 && (
                        <div style={{ fontSize: 10, color: '#475569' }}>
                          ₹{(colValue / 100000).toFixed(1)}L pipeline
                        </div>
                      )}
                    </div>

                    {/* Cards */}
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 60 }}>
                      {colLeads.map((lead, idx) => <LeadCard key={lead.id} lead={lead} index={idx} />)}
                      {provided.placeholder}
                    </div>

                    {/* Add button */}
                    <button style={{
                      marginTop: 8, padding: '7px', borderRadius: 8, border: '1px dashed rgba(0,0,0,0.08)',
                      background: 'none', color: '#475569', fontSize: 12, cursor: 'pointer', width: '100%',
                      transition: 'all 0.15s', flexShrink: 0,
                    }}
                      onMouseEnter={e => { e.target.style.borderColor = stage.color; e.target.style.color = stage.color; }}
                      onMouseLeave={e => { e.target.style.borderColor = 'rgba(0,0,0,0.08)'; e.target.style.color = '#475569'; }}
                    >
                      + Add
                    </button>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      {/* Lead quick view modal */}
      {selectedLead && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedLead(null)}>
          <div className="modal-box">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #7c3aed, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  {selectedLead.name[0]}
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{selectedLead.name}</h3>
                  <div style={{ fontSize: 12, color: '#475569' }}>{selectedLead.phone}</div>
                </div>
              </div>
              <button onClick={() => setSelectedLead(null)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Country', value: selectedLead.country || '—' },
                { label: 'Course', value: selectedLead.course || '—' },
                { label: 'Intake', value: selectedLead.intake || '—' },
                { label: 'Budget', value: selectedLead.budget ? `₹${Number(selectedLead.budget).toLocaleString('en-IN')}` : '—' },
                { label: 'IELTS', value: selectedLead.ielts || '—' },
                { label: 'Source', value: `${SOURCE_ICONS[selectedLead.source]} ${selectedLead.source}` },
              ].map(item => (
                <div key={item.label} style={{ background: 'rgba(0,0,0,0.04)', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: '#475569', fontWeight: 500, marginBottom: 3 }}>{item.label}</div>
                  <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <span className={`badge badge-${selectedLead.status}`}>{pipelineStages.find(s => s.id === selectedLead.status)?.name}</span>
              <span className={`badge badge-${selectedLead.priority}`}>{selectedLead.priority}</span>
              <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: selectedLead.score >= 80 ? '#10b981' : selectedLead.score >= 60 ? '#f59e0b' : '#ef4444' }}>Score: {selectedLead.score}</span>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}>📞 Call</button>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}>💬 WhatsApp</button>
              <button className="btn-ghost" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}>📧 Email</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
